/**
 * What a dead player can still do.
 *
 * The spirit is a silent presence — seen, never heard — and the one thing it
 * can do is leave. **Revival is an offer, not a sentence.**
 *
 * The confirmation states what it costs rather than asking "are you sure?",
 * because the choice is irreversible: after this **no magic reaches that
 * character** — not Raise Dead, not Resurrection, not Reincarnate. Window
 * expiry produces the identical end state, which is why this is one path and
 * not two.
 */

import { useEffect, useState } from 'react';

import { api, ApiError } from '../api.js';

export interface SpiritPanelProps {
  readonly campaignId: string;
  /** Called after the seat changes, so the page can reload its state. */
  readonly onMovedOn: () => void;
}

interface CharacterSummary {
  character: { id: string; name: string; deathStatus?: string };
  classes: { class: string; level: number }[];
}

export function SpiritPanel({ campaignId, onMovedOn }: SpiritPanelProps) {
  const [candidates, setCandidates] = useState<CharacterSummary[]>([]);
  const [chosen, setChosen] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<{ characters: CharacterSummary[] }>('/api/characters')
      .then((body) =>
        setCandidates(body.characters.filter((c) => (c.character.deathStatus ?? 'alive') === 'alive')),
      )
      .catch(() => setError('your characters could not be loaded'));
  }, []);

  return (
    <section className="space-y-2 rounded-md border border-neutral-300 p-3 text-sm dark:border-neutral-700">
      <h2 className="font-semibold">You are a spirit</h2>
      <p className="text-xs text-neutral-600 dark:text-neutral-400">
        You see the scene and cannot act, signal or speak. Your friends may still be able to
        bring you back.
      </p>

      {error && (
        <p role="alert" className="text-xs text-amber-800 dark:text-amber-300">
          {error}
        </p>
      )}

      {!confirming ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="rounded-md border border-neutral-300 px-3 py-1 dark:border-neutral-700"
        >
          Retire this character
        </button>
      ) : (
        <div className="space-y-2">
          {/* What it costs, plainly. Not "are you sure?". */}
          <p className="text-xs">
            If you retire this character, <strong>no magic reaches them again</strong> — not Raise
            Dead, not Resurrection, not Reincarnate. Your party will be told, so they stop
            spending spells and journeys on you.
          </p>
          <label className="block text-xs text-neutral-500 dark:text-neutral-400" htmlFor="replacement">
            Who takes the seat
          </label>
          <select
            id="replacement"
            value={chosen}
            onChange={(e) => setChosen(e.target.value)}
            className="rounded-md border border-neutral-300 bg-white px-2 py-1 dark:border-neutral-700 dark:bg-neutral-900"
          >
            <option value="">choose a character…</option>
            {candidates.map((c) => (
              <option key={c.character.id} value={c.character.id}>
                {c.character.name} ({c.classes.map((k) => `${k.class} ${k.level}`).join('/')})
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={chosen === ''}
              onClick={() => {
                void api
                  .post(`/api/campaigns/${campaignId}/reroll`, { characterId: chosen })
                  .then(onMovedOn)
                  .catch((e: unknown) =>
                    setError(e instanceof ApiError ? e.message : 'that could not be done'),
                  );
              }}
              className="rounded-md border border-neutral-300 px-3 py-1 disabled:opacity-40 dark:border-neutral-700"
            >
              Retire for good
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-md px-3 py-1 underline underline-offset-2"
            >
              Stay
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
